import { parseTableColumnWidth, type TableColumnWidthSpec } from './parseTableColumnWidth'

export const SWING_TABLE_MIN_COLUMN_WIDTH_PX = 40

export interface SwingTableColumnWidthInput {
  value: string
  width?: string
  constantColumnWidth?: boolean
  type?: string
}

type FlexColumn = {
  value: string
  partWeight: number
  spec: TableColumnWidthSpec
  type?: string
}

function normalizeColumnType(type: unknown): string {
  return String(type ?? '').trim().toUpperCase()
}

function minSwingTableColumnWidthPx(type: unknown, widthPx: number): number {
  const columnType = normalizeColumnType(type)
  if (columnType === 'BOOLEAN' || columnType === 'CHECKBOX') {
    return Math.max(widthPx, 48)
  }
  if (
    columnType === 'DATETIME' ||
    columnType === 'DATE' ||
    columnType === 'TIMESTAMP' ||
    columnType === 'TIME'
  ) {
    return Math.max(widthPx, 80)
  }
  if (columnType === 'LISTBOX' || columnType === 'LIST_BOX') {
    return Math.max(widthPx, 32, SWING_TABLE_MIN_COLUMN_WIDTH_PX)
  }
  return Math.max(widthPx, SWING_TABLE_MIN_COLUMN_WIDTH_PX)
}

function enforceTotalWidthCap(
  result: Record<string, number>,
  tableWidthPx: number,
): Record<string, number> {
  const keys = Object.keys(result)
  if (!keys.length || tableWidthPx <= 0) return result

  const scaled = { ...result }
  const total = keys.reduce((sum, key) => sum + scaled[key], 0)
  if (total <= tableWidthPx) return scaled

  const ratio = tableWidthPx / total
  for (const key of keys) {
    scaled[key] = Math.max(
      SWING_TABLE_MIN_COLUMN_WIDTH_PX,
      Math.round(scaled[key] * ratio),
    )
  }

  let scaledTotal = keys.reduce((sum, key) => sum + scaled[key], 0)
  if (scaledTotal <= tableWidthPx) return scaled

  const sortedKeys = [...keys].sort((a, b) => scaled[b] - scaled[a])
  let excess = scaledTotal - tableWidthPx
  for (const key of sortedKeys) {
    if (excess <= 0) break
    const reducible = scaled[key] - SWING_TABLE_MIN_COLUMN_WIDTH_PX
    if (reducible <= 0) continue
    const cut = Math.min(reducible, excess)
    scaled[key] -= cut
    excess -= cut
  }

  return scaled
}

/**
 * Распределяет ширину колонок как TableSizeManager.recalcWidth в Swing.
 */
export function resolveSwingTableColumnWidths(
  columns: SwingTableColumnWidthInput[],
  tableWidthPx: number,
  overrides?: Record<string, number> | null,
): Record<string, number> {
  if (!columns.length || tableWidthPx <= 0) {
    return {}
  }

  const result: Record<string, number> = {}
  let fixedWidthSum = 0
  const flexColumns: FlexColumn[] = []

  for (const column of columns) {
    const override = overrides?.[column.value]
    if (override != null && Number.isFinite(override) && override > 0) {
      result[column.value] = Math.round(override)
      fixedWidthSum += result[column.value]
      continue
    }

    const spec = parseTableColumnWidth(column.width, column.constantColumnWidth)
    if (!spec.wasSet || spec.value == null) {
      flexColumns.push({ value: column.value, partWeight: 1, spec, type: column.type })
      continue
    }

    if (spec.isPart) {
      flexColumns.push({ value: column.value, partWeight: spec.value, spec, type: column.type })
      continue
    }

    const px = Math.round(spec.value)
    result[column.value] = px
    fixedWidthSum += px
  }

  const flexSpace = Math.max(0, tableWidthPx - fixedWidthSum)
  const partsSum = flexColumns.reduce((sum, col) => sum + col.partWeight, 0)

  if (partsSum > 0) {
    const pxPerPart = flexSpace / partsSum
    for (const column of flexColumns) {
      const rawPx = Math.round(column.partWeight * pxPerPart)
      result[column.value] = minSwingTableColumnWidthPx(column.type, rawPx)
    }
  }

  return enforceTotalWidthCap(result, tableWidthPx)
}
